/* ============================================================================
 * Faturamento de Serviços — NFS-e (Pipeline de Faturamento)
 *
 * Portado de base-html `erpone_faturamento_de_servi_os_nfs_e`:
 *   Order ID | Customer (CNPJ) | Service Code (LC 116) | Gross (R$) | Net (R$) | Status.
 *
 * Contrato real consumido (fiscal-documents.dto.ts):
 *   GET /fiscal/nfse-pipeline?status=&page=&limit=
 *     → PaginatedNfsePipelineDto (data[] NfsePipelineItemDto)
 *
 * O pipeline lista pedidos de venda prontos para faturar (SalesOrderStatus
 * CONFIRMED / PARTIALLY_FULFILLED). O protótipo oferece "Transmit Selected" e
 * "Preview NFS-e / Emit Single NFS-e" — NÃO existem endpoints de emissão/
 * preview neste controller R2B (só a leitura do pipeline). Essas ações foram
 * omitidas; a coluna mostra a descrição do serviço (dado real) e o valor
 * bruto/líquido sem botões inventados.
 * ========================================================================== */

import { useState } from 'react';
import { useAuth } from '../../../shared/auth/AuthContext';
import { formatCurrency, formatDate, formatDocument, EMPTY } from '../../../shared/utils/format';
import { Badge, Button, LoadingSkeleton } from '@nexora/design-system';
import { useNfsePipeline } from './fiscalDocumentsHooks';
import { PIPELINE_STATUS_LABEL, PIPELINE_STATUS_VARIANT } from './fiscalStatusMaps';
import { FISCAL_DOCUMENTS_CSS } from './fiscalDocumentsCss';

const STATUS_FILTERS = ['ALL', 'CONFIRMED', 'PARTIALLY_FULFILLED', 'FULFILLED', 'INVOICED', 'DRAFT', 'CANCELLED'];

const PAGE_SIZE = 20;

export function NfsePipelinePage(): JSX.Element {
  const { tenant } = useAuth();
  const tenantId = tenant?.id ?? '';

  const [statusFilter, setStatusFilter] = useState('ALL');
  const [page, setPage] = useState(0);

  const { data, isLoading, error, refetch } = useNfsePipeline(tenantId, {
    status: statusFilter,
    page: page + 1,
    limit: PAGE_SIZE,
  });

  const items = data?.data ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="fiscal-page">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-md mb-lg">
        <div>
          <h1 className="font-display-lg text-display-lg text-on-surface m-0">
            Faturamento de Serviços (NFS-e)
          </h1>
          <p className="font-body-md text-body-md text-on-surface-variant mt-xs">
            Pedidos de venda prontos para faturar como NFS-e.
          </p>
        </div>
      </div>

      {/* ── Filtros ── */}
      <div className="fiscal-toolbar">
        <select
          className="fiscal-control fiscal-control--select"
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setPage(0);
          }}
          aria-label="Filtrar por status do pedido"
        >
          <option value="ALL">Todos os status</option>
          {STATUS_FILTERS.filter((s) => s !== 'ALL').map((s) => (
            <option key={s} value={s}>
              {PIPELINE_STATUS_LABEL[s] ?? s}
            </option>
          ))}
        </select>
        <span className="fiscal-count">
          {total} {total === 1 ? 'pedido' : 'pedidos'} no pipeline
        </span>
      </div>

      {/* ── Tabela ── */}
      <div className="fiscal-card">
        {isLoading && (
          <div style={{ padding: '1rem' }}>
            <LoadingSkeleton count={6} height="40px" />
          </div>
        )}

        {error && !isLoading && (
          <div className="fiscal-state">
            <p className="fiscal-state__title">Erro ao carregar o pipeline NFS-e</p>
            <p className="fiscal-state__detail">{(error as Error).message}</p>
            <Button variant="outline" size="sm" onClick={() => void refetch()}>
              Tentar novamente
            </Button>
          </div>
        )}

        {!isLoading && !error && items.length === 0 && (
          <div className="fiscal-state">
            <p className="fiscal-state__detail">
              Nenhum pedido no pipeline{statusFilter !== 'ALL' ? ' com o filtro atual' : ''}.
            </p>
          </div>
        )}

        {!isLoading && !error && items.length > 0 && (
          <>
            <div className="fiscal-table">
              <table>
                <thead>
                  <tr>
                    <th>Pedido</th>
                    <th>Cliente</th>
                    <th>CNPJ</th>
                    <th>Código serviço (LC 116)</th>
                    <th>Serviço</th>
                    <th style={{ textAlign: 'right' }}>Bruto (R$)</th>
                    <th style={{ textAlign: 'right' }}>Líquido (R$)</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => (
                    <tr key={item.orderId}>
                      <td className="fiscal-mono">{item.orderNumber ?? EMPTY}</td>
                      <td>{item.customerName ?? EMPTY}</td>
                      <td className="fiscal-mono">
                        {item.customerCnpj ? formatDocument(item.customerCnpj, 'CNPJ') : EMPTY}
                      </td>
                      <td className="fiscal-mono">{item.serviceCode ?? EMPTY}</td>
                      <td>{item.serviceDescription ?? EMPTY}</td>
                      <td className="fiscal-num">{formatCurrency(item.gross)}</td>
                      <td className="fiscal-num">{formatCurrency(item.net)}</td>
                      <td>
                        <Badge variant={PIPELINE_STATUS_VARIANT[item.status] ?? 'neutral'} size="sm">
                          {PIPELINE_STATUS_LABEL[item.status] ?? item.status}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="fiscal-footer">
              <span className="font-body-sm text-body-sm text-on-surface-variant tabular-nums">
                {page * PAGE_SIZE + 1}–{Math.min(total, page * PAGE_SIZE + items.length)} de{' '}
                {total}
                {total > 0 && (
                  <span className="fiscal-recipient-doc">Emissão mais recente: {formatDate(items[0]?.issuedAt)}</span>
                )}
              </span>
              <div className="flex gap-xs">
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={page === 0}
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                >
                  Anterior
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={page + 1 >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Próxima
                </Button>
              </div>
            </div>
          </>
        )}
      </div>

      <style>{FISCAL_DOCUMENTS_CSS}</style>
    </div>
  );
}